import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPaletteDragCategory } from './paletteDragContext';
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  ControlButton,
  Background,
  ReactFlowProvider,
  useReactFlow,
  useStore,
  useNodesInitialized,
  applyNodeChanges,
  applyEdgeChanges,
  getNodesBounds,
  getViewportForBounds,
  Node,
  Edge,
  OnConnect,
  Connection,
  NodeChange,
  EdgeChange,
  OnSelectionChangeParams,
  XYPosition,
  ConnectionLineType,
  Viewport,
} from 'reactflow';

import 'reactflow/dist/style.css';
import './WorkflowEditor.css';

import Sidebar, { type WorkflowTestRunPayload } from './Sidebar/Sidebar';
import LogsModal from '../workflows/LogsModal';
import {
  SIDEBAR_DRAG_MIME,
  SIDEBAR_CATEGORIES,
  isManualMcpPalettePayload,
  sidebarPayloadFromDragJson,
  type SidebarDragPayload,
  workflowBuilderRegistryFromPayload,
} from './Sidebar/sidebarPalette';
import CommonNode, { NodeData } from './CommonNode/CommonNode';
import ControlModal from './Modal/ControlModal';
import IndustryTemplatesModal from './flowBuilderTemplates';
import TriggerNode, { type TriggerNodeData } from './TriggerNode/TriggerNode';
import TriggerConfigPanel from './TriggerConfigPanel';
import LLMNode, { type LlmNodeData } from './LLMNode/LLMNode';
import LLMConfigPanel from './LLMConfigPanel';
import WorkflowConfigPanelFrame from './WorkflowConfigPanelFrame';
import {
  buildTriggerPreview,
  defaultTriggerSettings,
  isTriggersCategory,
  paletteTypeToTriggerKind,
} from './triggerWorkflowTypes';
import type { TriggerSettings } from './triggerWorkflowTypes';
import {
  MCP_SOFT_LIMIT,
  createBlankDatabaseStep,
  createMcpStepFromPalette,
  defaultLlmSettings,
  isLlmCategory,
  isMcpPaletteCategory,
  type FlowLlmNodePersistedData,
} from './llmWorkflowTypes';
import AgentNode from './AgentNode/AgentNode';
import AgentConfigPanel from './AgentConfigPanel';
import {
  AGENT_SOFT_OP_LIMIT,
  agentKindFromPaletteType,
  createAgentDataForKind,
  isAgentsCategory,
  slugifyAgentNodeName,
  type AgentOperation,
  type FlowAgentPersistedData,
} from './agentWorkflowTypes';
import ActionNode from './ActionNode/ActionNode';
import ActionConfigPanel from './ActionConfigPanel';
import {
  collectParentConnectorOutputHints,
  collectTriggerUpstreamTemplates,
  collectUpstreamVariableHints,
  createDefaultActionData,
  isActionsCategory,
  paletteTypeToActionKind,
  type FlowActionPersistedData,
} from './actionWorkflowTypes';
import FlowFxNode from './FlowFxNode/FlowFxNode';
import FlowFxConfigPanel from './FlowFxConfigPanel';
import {
  createDefaultFlowFxData,
  isFlowControlCategory,
  isUtilitiesCategory,
  type FlowFxPersistedData,
} from './flowFxWorkflowTypes';
import { buildComplexDemoWorkflow } from './complexDemoWorkflow';
import { SAMPLE_WORKFLOW_CATALOG, type SampleWorkflowId } from './sampleWorkflowTemplates';
import { buildWorkflowDefinitionString, parseWorkflowDefinitionString } from './workflowDefinitionSerialize';
import { serializeWorkflowExecutionV1 } from './workflowExecutionSchemaV1';
import {
  getWorkflowById,
  workflowApiErrorMessage,
  type WorkflowDetailDto,
} from './postWorkflowService';

export type FlowTriggerNodeData = TriggerNodeData & {
  settings: TriggerSettings;
};

function isFlowTargetHandle(handle: string | null | undefined): boolean {
  if (handle == null || handle === 'in') return true;
  return /^in-\d+$/.test(handle);
}

export type FlowNodeData =
  | NodeData
  | FlowTriggerNodeData
  | FlowLlmNodePersistedData
  | FlowAgentPersistedData
  | FlowActionPersistedData
  | FlowFxPersistedData;

type LlmCanvasHandlers = {
  patchLlm: (nodeId: string, patch: Partial<FlowLlmNodePersistedData>) => void;
  reorderLlmMcp: (nodeId: string, fromIndex: number, toIndex: number) => void;
  removeLlmMcp: (nodeId: string, stepId: string) => void;
  appendMcpToLlm: (nodeId: string, payload: SidebarDragPayload) => void;
};

type FlowCanvasProps = {
  onOpenModal: (id: string) => void;
  nodes: Node<FlowNodeData>[];
  setNodes: React.Dispatch<React.SetStateAction<Node<FlowNodeData>[]>>;
  edges: Edge[];
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  onSelectionChange: (params: OnSelectionChangeParams) => void;
  onDroppedNodeSelect?: (nodeId: string) => void;
  llmHandlers: LlmCanvasHandlers;
  onAgentPlacementRequest: (position: XYPosition, payload: SidebarDragPayload) => void;
  onViewportChange?: (viewport: Viewport) => void;
  /** Increment when the canvas should fit all nodes (load, reset route, sample, clear, etc.). */
  canvasLayoutFitToken?: number;
  /**
   * When set, keyboard delete via Backspace is disabled on the canvas so typing in side panels
   * cannot remove the selected node after focus leaves a field (React Flow default is Backspace).
   */
  suspendKeyboardDelete?: boolean;
};

const CANVAS_FIT_VIEW_OPTIONS = { padding: 0.15, duration: 0 } as const;

/** Same zoom as React Flow `fitView`, but pins the graph to a left gutter (vertically centered). */
function applyLeftAlignedFitView(
  getNodes: () => Node[],
  setViewport: (v: Viewport) => void,
  width: number,
  height: number,
  minZ: number,
  maxZ: number
) {
  const visible = getNodes().filter((n) => !n.hidden && n.width && n.height);
  if (visible.length === 0 || width <= 0 || height <= 0) return;
  const bounds = getNodesBounds(visible);
  const pad = CANVAS_FIT_VIEW_OPTIONS.padding ?? 0.15;
  const { zoom } = getViewportForBounds(bounds, width, height, minZ, maxZ, pad);
  const leftGutter = Math.max(28, width * 0.035);
  const x = leftGutter - bounds.x * zoom;
  const boundsCenterY = bounds.y + bounds.height / 2;
  const y = height / 2 - boundsCenterY * zoom;
  setViewport({ x, y, zoom });
}

function ReactFlowFitOnLayoutToken({
  token,
  applyLeftAlignedFit,
}: {
  token: number;
  applyLeftAlignedFit: () => void;
}) {
  const nodesInitialized = useNodesInitialized();
  const lastTokenRef = useRef(0);
  useEffect(() => {
    if (token <= 0 || !nodesInitialized) return;
    if (token === lastTokenRef.current) return;
    lastTokenRef.current = token;
    let canceled = false;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!canceled) applyLeftAlignedFit();
      });
    });
    return () => {
      canceled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [token, nodesInitialized, applyLeftAlignedFit]);
  return null;
}

const nodeTypes = {
  commonNode: CommonNode,
  triggerNode: TriggerNode,
  llmNode: LLMNode,
  agentNode: AgentNode,
  actionNode: ActionNode,
  flowFxNode: FlowFxNode,
};

const demoFlow = buildComplexDemoWorkflow();
const initialNodes: Node<FlowNodeData>[] = demoFlow.nodes as Node<FlowNodeData>[];
const initialEdges: Edge[] = demoFlow.edges;

export function sanitizeLoadedFlow(nodes: Node<FlowNodeData>[], edges: Edge[]) {
  const triggerIds = new Set(nodes.filter((n) => n.type === 'triggerNode').map((n) => n.id));
  const nextEdges = edges.filter((e) => !triggerIds.has(e.target));
  return { nodes, edges: nextEdges };
}

function buildMinimalManualTriggerFlow(): { nodes: Node<FlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('manual');
  const triggerData: FlowTriggerNodeData = {
    label: 'Manual',
    triggerKind: 'manual',
    preview: buildTriggerPreview('manual', settings),
    settings,
  };
  return {
    nodes: [
      {
        id: `trigger-${Date.now()}`,
        type: 'triggerNode',
        position: { x: 120, y: 200 },
        data: triggerData,
      },
    ],
    edges: [],
  };
}

function resolveIncomingInputHint(nodeId: string, edges: Edge[], flowNodes: Node<FlowNodeData>[]): string {
  const incoming = edges.filter((e) => e.target === nodeId && isFlowTargetHandle(e.targetHandle));
  if (incoming.length === 0) return '{{ previous.output }}';
  const srcNode = flowNodes.find((n) => n.id === incoming[0].source);
  if (!srcNode) return '{{ previous.output }}';
  if (srcNode.type === 'llmNode') {
    const d = srcNode.data as FlowLlmNodePersistedData;
    return `{{ ${d.nodeName}.output }}`;
  }
  if (srcNode.type === 'agentNode') {
    const d = srcNode.data as FlowAgentPersistedData;
    return `{{ ${d.nodeName}.output }}`;
  }
  if (srcNode.type === 'triggerNode') {
    const d = srcNode.data as FlowTriggerNodeData;
    const templates = collectTriggerUpstreamTemplates(d);
    return templates[0] ?? '{{ previous.output }}';
  }
  if (srcNode.type === 'actionNode') {
    const d = srcNode.data as FlowActionPersistedData;
    return `{{ ${d.nodeName}.result }}`;
  }
  if (srcNode.type === 'flowFxNode') {
    const d = srcNode.data as FlowFxPersistedData;
    return `{{ ${d.nodeName}.output }}`;
  }
  if (srcNode.type === 'commonNode') {
    const d = srcNode.data as NodeData;
    return `{{ ${slugifyAgentNodeName(d.label)}.output }}`;
  }
  return '{{ previous.output }}';
}

const FlowCanvas: React.FC<FlowCanvasProps> = ({
  onOpenModal,
  nodes,
  setNodes,
  edges,
  setEdges,
  onSelectionChange,
  onDroppedNodeSelect,
  llmHandlers,
  onAgentPlacementRequest,
  onViewportChange,
  canvasLayoutFitToken = 0,
  suspendKeyboardDelete = false,
}) => {
  const { project, getNode, getIntersectingNodes, getNodes, setViewport } = useReactFlow();
  const width = useStore((s) => s.width);
  const height = useStore((s) => s.height);
  const minZoom = useStore((s) => s.minZoom);
  const maxZoom = useStore((s) => s.maxZoom);
  const [agentDragBlocked, setAgentDragBlocked] = useState(false);
  /** Coalesce drag-over hit tests to one frame — avoids tight loops with React Flow layout / ResizeObserver. */
  const dragOverRafRef = useRef<number | null>(null);
  const latestDragOverRef = useRef<{ cat: string | null; flowPos: XYPosition }>({
    cat: null,
    flowPos: { x: 0, y: 0 },
  });

  useEffect(
    () => () => {
      if (dragOverRafRef.current != null) {
        cancelAnimationFrame(dragOverRafRef.current);
        dragOverRafRef.current = null;
      }
    },
    []
  );

  const runLeftAlignedFit = useCallback(() => {
    applyLeftAlignedFitView(getNodes, setViewport, width, height, minZoom, maxZoom);
  }, [getNodes, height, maxZoom, minZoom, setViewport, width]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((nds) => applyNodeChanges(changes, nds) as Node<FlowNodeData>[]);
    },
    [setNodes]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [setEdges]
  );

  const isValidConnection = useCallback(
    (connection: Connection) => {
      if (connection.source === connection.target) return false;
      const source = connection.source ? getNode(connection.source) : null;
      if (source?.type === 'flowFxNode') {
        const sd = source.data as FlowFxPersistedData;
        if (sd.kind === 'end') return false;
      }
      const target = connection.target ? getNode(connection.target) : null;
      if (target?.type === 'triggerNode') return false;
      if (target?.type === 'flowFxNode') {
        const td = target.data as FlowFxPersistedData;
        if (td.kind === 'comment') return false;
      }
      return true;
    },
    [getNode]
  );

  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      const source = params.source ? getNode(params.source) : null;
      if (source?.type === 'flowFxNode') {
        const sd = source.data as FlowFxPersistedData;
        if (sd.kind === 'end') return;
      }
      const target = params.target ? getNode(params.target) : null;
      if (target?.type === 'triggerNode') return;
      if (target?.type === 'flowFxNode') {
        const td = target.data as FlowFxPersistedData;
        if (td.kind === 'comment') return;
      }
      setEdges((eds) => addEdge(params, eds));
    },
    [getNode, setEdges]
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      let payload: SidebarDragPayload | null = null;
      const paletteRaw = event.dataTransfer.getData(SIDEBAR_DRAG_MIME);
      if (paletteRaw) {
        payload = sidebarPayloadFromDragJson(paletteRaw);
      }
      if (!payload) {
        const legacyType = event.dataTransfer.getData('application/reactflow');
        if (!legacyType) return;
        payload = {
          type: legacyType,
          label: `${legacyType} node`,
          icon: '📦',
          category: SIDEBAR_CATEGORIES.UTILITIES,
        };
      }

      const bounds = event.currentTarget.getBoundingClientRect();
      const flowPos = project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const hitRect = { x: flowPos.x - 4, y: flowPos.y - 4, width: 8, height: 8 };
      const intersecting = getIntersectingNodes(hitRect, true);
      const llmUnderCursor = intersecting.find((n) => n.type === 'llmNode');

      if (isMcpPaletteCategory(payload.category) && llmUnderCursor) {
        llmHandlers.appendMcpToLlm(llmUnderCursor.id, payload);
        return;
      }

      const isTrigger = isTriggersCategory(payload.category);
      const triggerKind = paletteTypeToTriggerKind(payload.type);

      if (isTrigger) {
        if (!triggerKind) return;

        const hasTrigger = nodes.some((n) => n.type === 'triggerNode');
        if (hasTrigger) {
          window.alert('Only one trigger is allowed per workflow. Remove the existing trigger first.');
          return;
        }

        const settings = defaultTriggerSettings(triggerKind);
        const preview = buildTriggerPreview(triggerKind, settings);
        const id = `trigger-${Date.now()}`;

        const newNode: Node<FlowTriggerNodeData> = {
          id,
          type: 'triggerNode',
          position: flowPos,
          selected: true,
          data: {
            label: payload.label,
            triggerKind,
            preview,
            settings,
            ...workflowBuilderRegistryFromPayload(payload),
          },
        };

        setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), newNode]);
        onDroppedNodeSelect?.(id);
        return;
      }

      if (isLlmCategory(payload.category)) {
        const id = `llm-${Date.now()}`;
        const llmSettings = defaultLlmSettings(payload.label, payload.type);
        const newNode: Node<FlowLlmNodePersistedData> = {
          id,
          type: 'llmNode',
          position: flowPos,
          selected: true,
          data: {
            label: payload.label,
            paletteType: payload.type,
            ...llmSettings,
            ...workflowBuilderRegistryFromPayload(payload),
          },
        };
        setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), newNode]);
        onDroppedNodeSelect?.(id);
        return;
      }

      if (isAgentsCategory(payload.category)) {
        if (llmUnderCursor) {
          window.alert('Agents cannot be placed inside an LLM node. Connect agents with edges instead.');
          return;
        }
        onAgentPlacementRequest(flowPos, payload);
        return;
      }

      if (isMcpPaletteCategory(payload.category)) {
        if (llmUnderCursor) {
          llmHandlers.appendMcpToLlm(llmUnderCursor.id, payload);
          return;
        }
        window.alert('Drop MCP blocks onto an LLM node (into the dashed area) to add them to its sequence.');
        return;
      }

      if (isActionsCategory(payload.category)) {
        if (!paletteTypeToActionKind(payload.type)) return;
        const actionData = createDefaultActionData({
          type: payload.type,
          label: payload.label,
          ...workflowBuilderRegistryFromPayload(payload),
        });
        if (!actionData) return;
        const id = `action-${Date.now()}`;
        const newNode: Node<FlowActionPersistedData> = {
          id,
          type: 'actionNode',
          position: flowPos,
          selected: true,
          data: actionData,
        };
        setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), newNode]);
        onDroppedNodeSelect?.(id);
        return;
      }

      if (isFlowControlCategory(payload.category) || isUtilitiesCategory(payload.category)) {
        const fxData = createDefaultFlowFxData({
          type: payload.type,
          label: payload.label,
          ...workflowBuilderRegistryFromPayload(payload),
        });
        if (fxData) {
          const id = `flowfx-${Date.now()}`;
          const newNode: Node<FlowFxPersistedData> = {
            id,
            type: 'flowFxNode',
            position: flowPos,
            selected: true,
            data: fxData,
          };
          setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), newNode]);
          onDroppedNodeSelect?.(id);
          return;
        }
      }

      const newNode: Node<FlowNodeData> = {
        id: `${Date.now()}`,
        type: 'commonNode',
        position: flowPos,
        data: {
          label: payload.label,
          icon: payload.icon,
          type: payload.type,
          onOpenModal,
          ...workflowBuilderRegistryFromPayload(payload),
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [
      getIntersectingNodes,
      llmHandlers,
      nodes,
      onAgentPlacementRequest,
      onDroppedNodeSelect,
      onOpenModal,
      project,
      setNodes,
    ]
  );

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    const cat = getPaletteDragCategory();
    const bounds = event.currentTarget.getBoundingClientRect();
    const flowPos = project({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    });
    latestDragOverRef.current = { cat, flowPos };
    if (dragOverRafRef.current != null) return;
    dragOverRafRef.current = requestAnimationFrame(() => {
      dragOverRafRef.current = null;
      const { cat: c, flowPos: fp } = latestDragOverRef.current;
      const hitRect = { x: fp.x - 4, y: fp.y - 4, width: 8, height: 8 };
      const overLlm = getIntersectingNodes(hitRect, true).some((n) => n.type === 'llmNode');
      setAgentDragBlocked((prev) => {
        const next = c === SIDEBAR_CATEGORIES.AGENTS && overLlm;
        return prev === next ? prev : next;
      });
    });
  };

  const clearAgentDragBlock = () => {
    if (dragOverRafRef.current != null) {
      cancelAnimationFrame(dragOverRafRef.current);
      dragOverRafRef.current = null;
    }
    setAgentDragBlocked(false);
  };

  const decoratedNodes = nodes.map((node) => {
    if (node.type === 'triggerNode') {
      return node;
    }
    if (node.type === 'agentNode') {
      const d = node.data as FlowAgentPersistedData;
      const incomingInputHint = resolveIncomingInputHint(node.id, edges, nodes);
      return { ...node, data: { ...d, incomingInputHint } };
    }
    if (node.type === 'actionNode') {
      const d = node.data as FlowActionPersistedData;
      const incomingInputHint = resolveIncomingInputHint(node.id, edges, nodes);
      return { ...node, data: { ...d, incomingInputHint } };
    }
    if (node.type === 'flowFxNode') {
      const d = node.data as FlowFxPersistedData;
      if (d.kind === 'comment') return node;
      const incomingInputHint = resolveIncomingInputHint(node.id, edges, nodes);
      return { ...node, data: { ...d, incomingInputHint } };
    }
    if (node.type === 'llmNode') {
      const d = node.data as FlowLlmNodePersistedData;
      const bound: LlmNodeData = {
        ...d,
        onPatch: (patch) => llmHandlers.patchLlm(node.id, patch),
        onReorderMcp: (from, to) => llmHandlers.reorderLlmMcp(node.id, from, to),
        onRemoveMcp: (stepId) => llmHandlers.removeLlmMcp(node.id, stepId),
        onDropMcpPalette: (p) => llmHandlers.appendMcpToLlm(node.id, p),
      };
      return { ...node, data: bound };
    }
    return {
      ...node,
      data: {
        ...node.data,
        onOpenModal,
      } as FlowNodeData,
    };
  });

  return (
    <div
      className={`workflow-canvas workflow-canvas--scrollable${agentDragBlocked ? ' workflow-canvas--agent-blocked' : ''}`}
    >
      <div
        className="workflow-canvas__rf-mount"
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={clearAgentDragBlock}
        onDragEnd={clearAgentDragBlock}
      >
        <ReactFlow
          style={{ width: '100%', height: '100%' }}
          nodes={decoratedNodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          isValidConnection={isValidConnection}
          onSelectionChange={onSelectionChange}
          nodeTypes={nodeTypes}
          connectionLineType={ConnectionLineType.SmoothStep}
          defaultEdgeOptions={{ type: 'smoothstep' }}
          fitView={false}
          defaultViewport={{ x: 40, y: 20, zoom: 0.4 }}
          minZoom={0.12}
          maxZoom={1.6}
          proOptions={{ hideAttribution: true }}
          deleteKeyCode={suspendKeyboardDelete ? null : 'Backspace'}
          onNodeDoubleClick={(_event, node) => {
            if (node.type === 'commonNode') {
              onOpenModal(node.id);
            }
          }}
          onMoveEnd={(_event, viewport) => {
            onViewportChange?.(viewport);
          }}
        >
          {canvasLayoutFitToken > 0 ? (
            <ReactFlowFitOnLayoutToken
              token={canvasLayoutFitToken}
              applyLeftAlignedFit={runLeftAlignedFit}
            />
          ) : null}
          <Controls
            showZoom
            showFitView={false}
            showInteractive
            position="bottom-left"
            className="workflow-rf-controls"
          >
            <ControlButton
              type="button"
              className="react-flow__controls-fitview"
              onClick={runLeftAlignedFit}
              title="Fit view"
              aria-label="fit view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 30" width="16" height="16" aria-hidden>
                <path
                  fill="currentColor"
                  d="M3.692 4.63c0-.53.4-.938.939-.938h5.215V0H4.708C2.13 0 0 2.054 0 4.63v5.216h3.692V4.631zM27.354 0h-5.2v3.692h5.17c.53 0 .984.4.984.939v5.215H32V4.631A4.624 4.624 0 0027.354 0zm.954 24.83c0 .532-.4.94-.939.94h-5.215v3.768h5.215c2.577 0 4.631-2.13 4.631-4.707v-5.139h-3.692v5.139zm-23.677.94c-.531 0-.939-.4-.939-.94v-5.138H0v5.139c0 2.577 2.13 4.707 4.708 4.707h5.138V25.77H4.631z"
                />
              </svg>
            </ControlButton>
          </Controls>
          <MiniMap zoomable pannable className="workflow-rf-minimap" />
          <Background />
        </ReactFlow>
      </div>
    </div>
  );
};

const DEFAULT_WORKFLOW_VIEWPORT: Viewport = { x: 40, y: 20, zoom: 0.4 };

const WorkflowEditor: React.FC = () => {
  const { workflowId: routeWorkflowId } = useParams<{ workflowId?: string }>();
  const minimalFlow = useMemo(() => buildMinimalManualTriggerFlow(), []);

  const [nodes, setNodes] = useState<Node<FlowNodeData>[]>(() =>
    routeWorkflowId?.trim() ? minimalFlow.nodes : initialNodes
  );
  const [edges, setEdges] = useState<Edge[]>(() =>
    routeWorkflowId?.trim() ? minimalFlow.edges : initialEdges
  );
  const [workflowViewport, setWorkflowViewport] = useState<Viewport>(DEFAULT_WORKFLOW_VIEWPORT);
  const [canvasLayoutFitToken, setCanvasLayoutFitToken] = useState(0);
  const [loadedDetail, setLoadedDetail] = useState<WorkflowDetailDto | null>(null);
  const [remoteLoadState, setRemoteLoadState] = useState<'none' | 'loading' | 'ready' | 'error'>(() =>
    routeWorkflowId?.trim() ? 'loading' : 'none'
  );
  const [remoteLoadError, setRemoteLoadError] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [modalNodeId, setModalNodeId] = useState<string | null>(null);
  const [showTestRunLogsModal, setShowTestRunLogsModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [configPanelExpanded, setConfigPanelExpanded] = useState(false);
  const toggleConfigPanelExpand = useCallback(() => setConfigPanelExpanded((v) => !v), []);

  const sidebarRemotePrefill = useMemo(() => {
    if (!loadedDetail) return null;
    return {
      name: loadedDetail.name ?? '',
      icon: loadedDetail.icon?.trim() ? loadedDetail.icon : 'Workflow',
      description: loadedDetail.description ?? '',
      notes: loadedDetail.notes ?? '',
      lastComments: loadedDetail.last_comments ?? '',
      schemaVersion: loadedDetail.schema_version ?? 0,
      isActive: loadedDetail.is_active !== false,
      projectUuid: loadedDetail.project_uuid?.trim() ?? '',
    };
  }, [loadedDetail]);

  const editingWorkflowIdForSave =
    routeWorkflowId?.trim() && remoteLoadState === 'ready' ? routeWorkflowId.trim() : null;

  useEffect(() => {
    const id = routeWorkflowId?.trim();
    if (!id) {
      setRemoteLoadState('none');
      setLoadedDetail(null);
      setRemoteLoadError(null);
      setNodes(initialNodes);
      setEdges(initialEdges);
      setWorkflowViewport(DEFAULT_WORKFLOW_VIEWPORT);
      setCanvasLayoutFitToken((t) => t + 1);
      return;
    }

    let cancelled = false;
    setRemoteLoadState('loading');
    setRemoteLoadError(null);
    setLoadedDetail(null);

    (async () => {
      try {
        const dto = await getWorkflowById(id);
        if (cancelled) return;
        setLoadedDetail(dto);
        const parsed = parseWorkflowDefinitionString(dto.definition);
        if (parsed) {
          const typed = parsed.nodes as Node<FlowNodeData>[];
          const { nodes: nextNodes, edges: nextEdges } = sanitizeLoadedFlow(typed, parsed.edges);
          setNodes(nextNodes);
          setEdges(nextEdges);
          setWorkflowViewport(parsed.viewport);
        } else {
          const { nodes: nextNodes, edges: nextEdges } = sanitizeLoadedFlow(
            minimalFlow.nodes,
            minimalFlow.edges
          );
          setNodes(nextNodes);
          setEdges(nextEdges);
          setWorkflowViewport(DEFAULT_WORKFLOW_VIEWPORT);
        }
        setCanvasLayoutFitToken((t) => t + 1);
        setSelectedNodeId(null);
        setRemoteLoadState('ready');
      } catch (e) {
        if (cancelled) return;
        setRemoteLoadState('error');
        setRemoteLoadError(workflowApiErrorMessage(e));
        const { nodes: nextNodes, edges: nextEdges } = sanitizeLoadedFlow(
          minimalFlow.nodes,
          minimalFlow.edges
        );
        setNodes(nextNodes);
        setEdges(nextEdges);
        setWorkflowViewport(DEFAULT_WORKFLOW_VIEWPORT);
        setCanvasLayoutFitToken((t) => t + 1);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeWorkflowId, minimalFlow]);

  const onSelectionChange = useCallback((params: OnSelectionChangeParams) => {
    const first = params.nodes[0];
    setSelectedNodeId(first?.id ?? null);
  }, []);

  const openModal = (nodeId: string) => {
    setModalNodeId(nodeId);
  };

  const closeModal = () => setModalNodeId(null);

  const clearCanvasSelection = useCallback(() => {
    setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
    setSelectedNodeId(null);
  }, [setNodes]);

  const updateTriggerNode = useCallback(
    (nodeId: string, partial: Partial<FlowTriggerNodeData>) => {
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId || n.type !== 'triggerNode') return n;
          const d = n.data as FlowTriggerNodeData;
          const settings = (partial.settings ?? d.settings) as TriggerSettings;
          const label = partial.label ?? d.label;
          const preview =
            partial.preview ??
            (partial.settings ? buildTriggerPreview(settings.kind, settings) : d.preview);
          return {
            ...n,
            data: {
              ...d,
              ...partial,
              label,
              settings,
              preview,
            },
          };
        })
      );
    },
    [setNodes]
  );

  const deleteTriggerNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNodeId((id) => (id === nodeId ? null : id));
    },
    [setNodes, setEdges]
  );

  const patchLlmNode = useCallback((nodeId: string, patch: Partial<FlowLlmNodePersistedData>) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId || n.type !== 'llmNode') return n;
        const d = n.data as FlowLlmNodePersistedData;
        return { ...n, data: { ...d, ...patch } };
      })
    );
  }, [setNodes]);

  const reorderLlmMcp = useCallback((nodeId: string, from: number, to: number) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId || n.type !== 'llmNode') return n;
        const d = n.data as FlowLlmNodePersistedData;
        const arr = [...d.mcpSteps];
        if (from < 0 || from >= arr.length || to < 0 || to > arr.length) return n;
        const [item] = arr.splice(from, 1);
        arr.splice(to, 0, item);
        return { ...n, data: { ...d, mcpSteps: arr } };
      })
    );
  }, [setNodes]);

  const removeLlmMcp = useCallback((nodeId: string, stepId: string) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId || n.type !== 'llmNode') return n;
        const d = n.data as FlowLlmNodePersistedData;
        return {
          ...n,
          data: { ...d, mcpSteps: d.mcpSteps.filter((s) => s.id !== stepId) },
        };
      })
    );
  }, [setNodes]);

  const appendMcpToLlm = useCallback((nodeId: string, payload: SidebarDragPayload) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId || n.type !== 'llmNode') return n;
        const d = n.data as FlowLlmNodePersistedData;
        if (d.mcpSteps.length >= MCP_SOFT_LIMIT) {
          window.alert('Soft limit: at most 10 MCP steps per LLM.');
          return n;
        }
        const keys = new Set(d.mcpSteps.map((s) => s.outputKey));
        const step = createMcpStepFromPalette(payload, keys);
        if (!step) return n;
        return { ...n, data: { ...d, mcpSteps: [...d.mcpSteps, step] } };
      })
    );
  }, [setNodes]);

  const addBlankMcpStep = useCallback((nodeId: string) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId || n.type !== 'llmNode') return n;
        const d = n.data as FlowLlmNodePersistedData;
        if (d.mcpSteps.length >= MCP_SOFT_LIMIT) {
          window.alert('Soft limit: at most 10 MCP steps per LLM.');
          return n;
        }
        const keys = new Set(d.mcpSteps.map((s) => s.outputKey));
        const step = createBlankDatabaseStep(keys);
        return { ...n, data: { ...d, mcpSteps: [...d.mcpSteps, step] } };
      })
    );
  }, [setNodes]);

  const deleteLlmNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNodeId((id) => (id === nodeId ? null : id));
    },
    [setNodes, setEdges]
  );

  const llmHandlers = useMemo(
    () => ({
      patchLlm: patchLlmNode,
      reorderLlmMcp,
      removeLlmMcp,
      appendMcpToLlm,
    }),
    [appendMcpToLlm, patchLlmNode, removeLlmMcp, reorderLlmMcp]
  );

  const onAgentPlacementRequest = useCallback(
    (position: XYPosition, payload: SidebarDragPayload) => {
      const kind = agentKindFromPaletteType(payload.type);
      const id = `agent-${Date.now()}`;
      const data = createAgentDataForKind(
        kind,
        { type: payload.type, label: payload.label },
        workflowBuilderRegistryFromPayload(payload)
      );
      const newNode: Node<FlowAgentPersistedData> = {
        id,
        type: 'agentNode',
        position,
        selected: true,
        data,
      };
      setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), newNode]);
      setSelectedNodeId(id);
    },
    [setNodes, setSelectedNodeId]
  );

  const patchAgentNode = useCallback((nodeId: string, patch: Partial<FlowAgentPersistedData>) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId || n.type !== 'agentNode') return n;
        return { ...n, data: { ...(n.data as FlowAgentPersistedData), ...patch } };
      })
    );
  }, [setNodes]);

  const reorderAgentOps = useCallback((nodeId: string, from: number, to: number) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId || n.type !== 'agentNode') return n;
        const d = n.data as FlowAgentPersistedData;
        const arr = [...d.operations];
        if (from < 0 || from >= arr.length || to < 0 || to > arr.length) return n;
        const [item] = arr.splice(from, 1);
        arr.splice(to, 0, item);
        return { ...n, data: { ...d, operations: arr } };
      })
    );
  }, [setNodes]);

  const removeAgentOp = useCallback((nodeId: string, opId: string) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId || n.type !== 'agentNode') return n;
        const d = n.data as FlowAgentPersistedData;
        if (d.operations.length <= 1) {
          window.alert('At least one operation is required.');
          return n;
        }
        return { ...n, data: { ...d, operations: d.operations.filter((o) => o.id !== opId) } };
      })
    );
  }, [setNodes]);

  const addAgentOp = useCallback((nodeId: string, op: Omit<AgentOperation, 'id'>) => {
    const id = `op-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId || n.type !== 'agentNode') return n;
        const d = n.data as FlowAgentPersistedData;
        if (d.operations.length >= AGENT_SOFT_OP_LIMIT) {
          window.alert(`Soft limit: at most ${AGENT_SOFT_OP_LIMIT} operations per agent.`);
          return n;
        }
        return { ...n, data: { ...d, operations: [...d.operations, { ...op, id }] } };
      })
    );
  }, [setNodes]);

  const deleteAgentNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNodeId((id) => (id === nodeId ? null : id));
    },
    [setNodes, setEdges]
  );

  const patchActionNode = useCallback((nodeId: string, partial: Partial<FlowActionPersistedData>) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId || n.type !== 'actionNode') return n;
        return { ...n, data: { ...(n.data as FlowActionPersistedData), ...partial } };
      })
    );
  }, [setNodes]);

  const deleteActionNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNodeId((id) => (id === nodeId ? null : id));
    },
    [setNodes, setEdges]
  );

  const patchFlowFxNode = useCallback((nodeId: string, partial: Partial<FlowFxPersistedData>) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId || n.type !== 'flowFxNode') return n;
        return { ...n, data: { ...(n.data as FlowFxPersistedData), ...partial } };
      })
    );
  }, [setNodes]);

  const deleteFlowFxNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNodeId((id) => (id === nodeId ? null : id));
    },
    [setNodes, setEdges]
  );

  const saveNodeData = (label: string, type: string, settings: Record<string, unknown>) => {
    if (!modalNodeId) return;
    setNodes((nds) =>
      nds.map((node) =>
        node.id === modalNodeId
          ? {
              ...node,
              data: {
                ...node.data,
                label,
                type,
                settings,
              } as FlowNodeData,
            }
          : node
      )
    );
    setModalNodeId(null);
  };

  const deleteModalNode = () => {
    if (!modalNodeId) return;
    setNodes((nds) => nds.filter((node) => node.id !== modalNodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== modalNodeId && edge.target !== modalNodeId));
    setModalNodeId(null);
  };

  const saveFlow = () => {
    const flowData = { nodes, edges };
    localStorage.setItem('flowData', JSON.stringify(flowData));
    window.alert('Flow saved successfully.');
  };

  const loadFlow = () => {
    const flowData = localStorage.getItem('flowData');
    if (flowData) {
      const parsed = JSON.parse(flowData) as { nodes?: Node<FlowNodeData>[]; edges?: Edge[] };
      const rawNodes = parsed.nodes || [];
      const rawEdges = parsed.edges || [];
      const { nodes: nextNodes, edges: nextEdges } = sanitizeLoadedFlow(rawNodes, rawEdges);
      setNodes(nextNodes);
      setEdges(nextEdges);
      setSelectedNodeId(null);
      setCanvasLayoutFitToken((t) => t + 1);
      window.alert('Flow loaded successfully.');
    } else {
      window.alert('No saved flow found.');
    }
  };

  const applySampleWorkflow = useCallback(
    (id: SampleWorkflowId) => {
      const entry = SAMPLE_WORKFLOW_CATALOG.find((c) => c.id === id);
      if (!entry) return;
      const { nodes: nextNodes, edges: rawEdges } = entry.build();
      const { nodes: sanitizedNodes, edges: nextEdges } = sanitizeLoadedFlow(
        nextNodes as Node<FlowNodeData>[],
        rawEdges
      );
      setNodes(sanitizedNodes);
      setEdges(nextEdges);
      setSelectedNodeId(null);
      setCanvasLayoutFitToken((t) => t + 1);
    },
    [setEdges, setNodes]
  );

  const clearCanvas = useCallback(() => {
    if (!window.confirm('Clear the canvas and reset to a single manual trigger?')) return;
    const { nodes: rawNodes, edges: rawEdges } = buildMinimalManualTriggerFlow();
    const { nodes: nextNodes, edges: nextEdges } = sanitizeLoadedFlow(rawNodes, rawEdges);
    setNodes(nextNodes);
    setEdges(nextEdges);
    setSelectedNodeId(null);
    setCanvasLayoutFitToken((t) => t + 1);
  }, [setEdges, setNodes]);

  const testWorkflow = useCallback((_payload?: WorkflowTestRunPayload) => {
    setShowTestRunLogsModal(true);
  }, []);

  /** Same JSON string as save API `definition`; Sidebar FileJson modal calls this to preview/copy. */
  const buildWorkflowSaveDefinition = useCallback(() => {
    return buildWorkflowDefinitionString(nodes, edges, workflowViewport);
  }, [nodes, edges, workflowViewport]);

  const buildExecutionSchemaPreview = useCallback(() => {
    return serializeWorkflowExecutionV1(nodes, edges, {
      sourceFlowId: editingWorkflowIdForSave ?? routeWorkflowId?.trim() ?? null,
      tenantId: loadedDetail?.tenant_id ?? null,
    });
  }, [nodes, edges, editingWorkflowIdForSave, routeWorkflowId, loadedDetail?.tenant_id]);

  const selectedNode = selectedNodeId ? nodes.find((n) => n.id === selectedNodeId) : undefined;
  const selectedTrigger =
    selectedNode?.type === 'triggerNode' ? (selectedNode as Node<FlowTriggerNodeData>) : null;
  const selectedLlm =
    selectedNode?.type === 'llmNode' ? (selectedNode as Node<FlowLlmNodePersistedData>) : null;
  const selectedAgent =
    selectedNode?.type === 'agentNode' ? (selectedNode as Node<FlowAgentPersistedData>) : null;
  const selectedAction =
    selectedNode?.type === 'actionNode' ? (selectedNode as Node<FlowActionPersistedData>) : null;
  const selectedFlowFx =
    selectedNode?.type === 'flowFxNode' ? (selectedNode as Node<FlowFxPersistedData>) : null;
  const actionUpstreamHints = useMemo(
    () =>
      selectedAction ? collectUpstreamVariableHints(selectedAction.id, edges, nodes) : [],
    [edges, nodes, selectedAction]
  );
  const flowFxUpstreamHints = useMemo(
    () =>
      selectedFlowFx && selectedFlowFx.data.kind !== 'comment'
        ? collectUpstreamVariableHints(selectedFlowFx.id, edges, nodes)
        : [],
    [edges, nodes, selectedFlowFx]
  );

  useEffect(() => {
    setConfigPanelExpanded(false);
  }, [selectedNodeId]);

  const modalNode = modalNodeId ? nodes.find((n) => n.id === modalNodeId) : undefined;

  return (
    <div className="workflow-editor-root">
      {showTestRunLogsModal && editingWorkflowIdForSave ? (
        <LogsModal
          key={`test-run-logs-${editingWorkflowIdForSave}`}
          workflowId={editingWorkflowIdForSave}
          name={loadedDetail?.name?.trim() || 'Workflow'}
          initialAutoRefreshList
          initialOpenLatestRunTimeline
          onClose={() => setShowTestRunLogsModal(false)}
        />
      ) : null}
      {remoteLoadState === 'loading' && routeWorkflowId?.trim() ? (
        <div className="workflow-editor-remote-banner workflow-editor-remote-banner--loading" role="status">
          Loading workflow…
        </div>
      ) : null}
      {remoteLoadState === 'error' && remoteLoadError ? (
        <div className="workflow-editor-remote-banner workflow-editor-remote-banner--error" role="alert">
          {remoteLoadError}
        </div>
      ) : null}
      <div className="workflow-container">
        <Sidebar
          onApplySampleWorkflow={applySampleWorkflow}
          onClearCanvas={clearCanvas}
          onTestWorkflow={testWorkflow}
          buildWorkflowSaveDefinition={buildWorkflowSaveDefinition}
          buildExecutionSchemaPreview={buildExecutionSchemaPreview}
          editingWorkflowId={editingWorkflowIdForSave}
          persistedWorkflowRouteId={routeWorkflowId?.trim() ?? null}
          remoteWorkflowPrefill={sidebarRemotePrefill}
          remoteWorkflowLoadState={remoteLoadState}
        />
        <div className="workflow-main workflow-main--with-panel">
          {/* <div className="workflow-topbar">
            <div className="workflow-actions">
              <button type="button" onClick={saveFlow} className="btn save-btn" title="Save flow">
                💾
              </button>
              <button type="button" onClick={loadFlow} className="btn load-btn" title="Load saved flow">
                📥
              </button>
              <button
                type="button"
                onClick={() => setShowTemplatesModal(true)}
                className="btn load-btn"
                title="Industry templates"
              >
                📂
              </button>
            </div>
          </div> */}

          <div className="workflow-flow-host">
            <ReactFlowProvider>
              <FlowCanvas
                nodes={nodes}
                setNodes={setNodes}
                edges={edges}
                setEdges={setEdges}
                onOpenModal={openModal}
                onSelectionChange={onSelectionChange}
                onDroppedNodeSelect={setSelectedNodeId}
                llmHandlers={llmHandlers}
                onAgentPlacementRequest={onAgentPlacementRequest}
                onViewportChange={setWorkflowViewport}
                canvasLayoutFitToken={canvasLayoutFitToken}
                suspendKeyboardDelete={Boolean(selectedNodeId)}
              />
            </ReactFlowProvider>
          </div>
        </div>

        {selectedTrigger && (
          <WorkflowConfigPanelFrame expanded={configPanelExpanded}>
            <TriggerConfigPanel
              node={selectedTrigger}
              panelMaximized={configPanelExpanded}
              onTogglePanelMaximize={toggleConfigPanelExpand}
              onUpdate={updateTriggerNode}
              onDelete={deleteTriggerNode}
              onClose={clearCanvasSelection}
            />
          </WorkflowConfigPanelFrame>
        )}

        {selectedLlm && !selectedTrigger && (
          <WorkflowConfigPanelFrame expanded={configPanelExpanded}>
            <LLMConfigPanel
              node={selectedLlm}
              panelMaximized={configPanelExpanded}
              onTogglePanelMaximize={toggleConfigPanelExpand}
              parentConnectorOutputHints={collectParentConnectorOutputHints(selectedLlm.id, edges, nodes)}
              onUpdate={patchLlmNode}
              onReorderMcp={reorderLlmMcp}
              onRemoveMcp={removeLlmMcp}
              onAddBlankMcp={addBlankMcpStep}
              onDelete={deleteLlmNode}
              onClose={clearCanvasSelection}
            />
          </WorkflowConfigPanelFrame>
        )}

        {selectedAgent && !selectedTrigger && !selectedLlm && (
          <WorkflowConfigPanelFrame expanded={configPanelExpanded}>
            <AgentConfigPanel
              node={selectedAgent}
              panelMaximized={configPanelExpanded}
              onTogglePanelMaximize={toggleConfigPanelExpand}
              onUpdate={patchAgentNode}
              onReorderOps={reorderAgentOps}
              onRemoveOp={removeAgentOp}
              onAddOp={addAgentOp}
              onDelete={deleteAgentNode}
              onClose={clearCanvasSelection}
            />
          </WorkflowConfigPanelFrame>
        )}

        {selectedAction && !selectedTrigger && !selectedLlm && !selectedAgent && (
          <WorkflowConfigPanelFrame expanded={configPanelExpanded}>
            <ActionConfigPanel
              node={selectedAction}
              panelMaximized={configPanelExpanded}
              onTogglePanelMaximize={toggleConfigPanelExpand}
              upstreamHints={actionUpstreamHints}
              onUpdate={patchActionNode}
              onDelete={deleteActionNode}
              onClose={clearCanvasSelection}
            />
          </WorkflowConfigPanelFrame>
        )}

        {selectedFlowFx &&
          !selectedTrigger &&
          !selectedLlm &&
          !selectedAgent &&
          !selectedAction && (
            <WorkflowConfigPanelFrame expanded={configPanelExpanded}>
              <FlowFxConfigPanel
                node={selectedFlowFx}
                panelMaximized={configPanelExpanded}
                onTogglePanelMaximize={toggleConfigPanelExpand}
                upstreamHints={flowFxUpstreamHints}
                onUpdate={patchFlowFxNode}
                onDelete={deleteFlowFxNode}
                onClose={clearCanvasSelection}
              />
            </WorkflowConfigPanelFrame>
          )}
      </div>

      {modalNodeId && modalNode && modalNode.type === 'commonNode' && (
        <ControlModal
          node={modalNode as Node<NodeData>}
          onSave={saveNodeData}
          onDelete={deleteModalNode}
          onClose={closeModal}
        />
      )}

      {showTemplatesModal && (
        <IndustryTemplatesModal onClose={() => setShowTemplatesModal(false)} />
      )}
    </div>
  );
};

export default WorkflowEditor;
